import { useCallback, useEffect, useRef, useState } from 'react'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { sessionGone } from '../../lib/errors'
import { lastActivity, onActivity } from '../lib/activity'
import { useI18n } from '../lib/i18n'
import { sessionPhase } from '../lib/session-clock'
import { endStaffSession } from '../lib/session-end'
import { useSignOut, type SignedInState } from '../lib/session'
import { useTRPCClient } from '../lib/trpc'

/*
 * The session's time limits on screen (ADR 0008 §4): the person's presses and keys keep the
 * session in use (session.keepAlive, at most once a minute); 2 minutes before the 30 idle minutes
 * a dialog asks whether they are still there, and 10 minutes before the 8 hours a notice says
 * when the session ends. Once a limit passes, the page asks the server, which closes the session,
 * and the sign-in page says why. The clock is checked every few seconds and whenever the tab is
 * shown again, so a sleeping laptop or a tab in the background catch up at once.
 */

const TICK_MS = 5_000
/** How often the person's activity is told to the server, at most. */
const KEEP_ALIVE_EVERY_MS = 60_000
const PRESENCE_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

export function SessionWatch({ session }: { session: SignedInState }) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const signOut = useSignOut()
  const endsAt = Date.parse(session.endsAt)
  const { idleMinutes } = session
  const [now, setNow] = useState(Date.now)
  const [last, setLast] = useState(lastActivity)
  const [snoozed, setSnoozed] = useState<number | null>(null)
  const sending = useRef(false)

  useEffect(() => onActivity(setLast), [])

  useEffect(() => {
    const tick = () => setNow(Date.now())
    const id = window.setInterval(tick, TICK_MS)
    document.addEventListener('visibilitychange', tick)
    window.addEventListener('focus', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
      window.removeEventListener('focus', tick)
    }
  }, [])

  /** Tells the server the person is here (the answer records the activity, lib/trpc.ts). */
  const keepAlive = useCallback(() => {
    if (sending.current) return
    sending.current = true
    client.session.keepAlive
      .query()
      .catch((err: unknown) => {
        if (sessionGone(err)) endStaffSession()
      })
      .finally(() => {
        sending.current = false
      })
  }, [client])

  // Opening or reloading the page is the person's doing; then, their presses and keys.
  useEffect(() => {
    keepAlive()
    const onPresence = () => {
      if (Date.now() - lastActivity() >= KEEP_ALIVE_EVERY_MS) keepAlive()
    }
    for (const event of PRESENCE_EVENTS)
      window.addEventListener(event, onPresence, { capture: true, passive: true })
    return () => {
      for (const event of PRESENCE_EVENTS)
        window.removeEventListener(event, onPresence, { capture: true })
    }
  }, [keepAlive])

  const phase = sessionPhase({ now, lastActivity: last, idleMinutes, endsAt })

  // A limit has passed: the server decides (auth.state closes an idle or expired session and
  // answers that there is none). Asked again on each tick while it still answers a session.
  const { over } = phase
  useEffect(() => {
    if (!over) return
    let cancelled = false
    client.auth.state
      .query()
      .then((state) => {
        if (!cancelled && state.status !== 'signed-in')
          endStaffSession(over === 'expired' ? 'caducada' : 'inactividad')
      })
      .catch(() => {
        // Not reachable now: the next tick asks again.
      })
    return () => {
      cancelled = true
    }
  }, [over, now, client])

  return (
    <>
      {phase.endingSoon && (
        <p
          role="status"
          className="border-b border-line bg-accent/20 px-4 py-2 text-center text-sm"
        >
          {t('ending.notice', { endsAt: new Date(endsAt) })}
        </p>
      )}
      <ConfirmDialog
        open={phase.idleWarning && snoozed !== phase.idleAt}
        onOpenChange={(open) => {
          if (open) return
          // Closing it, by its button or Escape, keeps the session.
          setSnoozed(phase.idleAt)
          keepAlive()
        }}
        title={t('idle.title')}
        cancelLabel={t('idle.stay')}
        confirmLabel={t('common.signOut')}
        onConfirm={() => signOut.mutate()}
      >
        <p>{t('idle.body', { at: new Date(phase.idleAt) })}</p>
      </ConfirmDialog>
    </>
  )
}
